import { buildHorseSubtitle } from '../utils/horseSubtitle';
/**
 * HorseList — Liste des chevaux du propriétaire avec photo, infos et actions
 *
 * App.jsx ne rend ce composant que si le user est propriétaire (signup role)
 * OU s'il possède au moins un cheval. Conséquence : tous les chevaux ici sont
 * possédés par le user (rôle "owner" per-horse), donc pas de check de droits
 * dans la liste — toutes les actions sont disponibles.
 */
import { getTheme, TYPE, RADIUS, SHADOW, BTN } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';
import React from 'react';
import ProtectedImage from '../shared/ProtectedImage';
import { FileText, BarChart3, Upload, Pencil, ClipboardList, Radio, Trash2 } from 'lucide-react';
import HorseIcon from '../shared/HorseIcon';

const PHOTO_SIZE = 72;

const HorseList = ({ horses, darkMode, onViewData, onUploadCSV, onEdit, onDelete, onHealth, onViewDetail, onLive }) => {
  const t = getTheme(darkMode);
  const cardStyle = {
    background: t.card,
    padding: '30px',
    borderRadius: '8px',
    border: `1px solid ${t.border}`,
    boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '20px'
  };

  const horseCardStyle = {
    background: t.surface,
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${t.border}`,
    marginBottom: '12px'
  };

  // Styles boutons — voir src/constants/theme.js BTN.*

  const dangerButtonStyle = {
    ...BTN.secondary(darkMode),
    color: semanticColors.redText,
    borderColor: '#EF4444'
  };

  const photoStyle = {
    width: PHOTO_SIZE,
    height: PHOTO_SIZE,
    borderRadius: '50%',
    border: `2px solid ${t.border}`,
    flexShrink: 0
  };

  const avatarStyle = {
    ...photoStyle,
    background: darkMode ? '#282845' : '#e8f0fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px'
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ color: '#1B77F1', marginBottom: '20px', fontSize: '20px' }}>
        Mes Chevaux
      </h2>

      {horses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px 24px', color: t.textSecondary }}>
          <p style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>Aucun cheval enregistré</p>
          <p style={{ fontSize: '14px' }}>Ajoutez votre premier cheval en cliquant sur le bouton <strong>+ Nouveau cheval</strong> dans le menu.</p>
        </div>
      ) : (
        <div>
          {horses.map((horse) => (
            <div key={horse._id} style={horseCardStyle}>
              {/* Ligne 1 : Photo + Infos */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                {horse.photo1 ? (
                  <ProtectedImage
                    src={`/uploads/horses/${horse.photo1}`}
                    alt={horse.horseName}
                    style={{ ...photoStyle, objectFit: 'cover' }}
                  />
                ) : null}
                <div style={{ ...avatarStyle, display: horse.photo1 ? 'none' : 'flex' }}>
                  <HorseIcon size={40} color={t.accent} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    color: t.textPrimary,
                    marginBottom: '4px', fontSize: '17px', fontWeight: '600',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {horse.horseName}
                  </h3>
                  {buildHorseSubtitle(horse) && (
                    <p style={{
                      color: t.textSecondary, fontSize: '13px', margin: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {buildHorseSubtitle(horse)}
                    </p>
                  )}
                </div>
              </div>

              {/* Ligne 2 : Boutons — grille 2×2 sur mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                <button onClick={() => onViewDetail && onViewDetail(horse._id)} style={{ ...BTN.primary, width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <FileText size={15} /> Voir fiche
                </button>
                <button onClick={() => onViewData(horse._id)} style={{ ...BTN.secondary(darkMode), width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <BarChart3 size={15} /> Données
                </button>
                <button onClick={() => onUploadCSV(horse._id)} style={{ ...BTN.secondary(darkMode), width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Upload size={15} /> Upload
                </button>
                <button onClick={() => onEdit && onEdit(horse._id)} style={{ ...BTN.secondary(darkMode), width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Pencil size={15} /> Modifier
                </button>
                <button onClick={() => onHealth && onHealth(horse._id)} style={{ ...BTN.secondary(darkMode), width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <ClipboardList size={15} /> Santé
                </button>
                {horse.equipmentId && (
                  <button onClick={() => onLive && onLive(horse._id)} style={{ ...BTN.primary, width: '100%', minHeight: '44px', background: '#10B981', borderColor: '#10B981', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <Radio size={15} /> Live
                  </button>
                )}
                <button onClick={() => onDelete && onDelete(horse._id)} style={{ ...dangerButtonStyle, width: '100%', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Trash2 size={15} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HorseList;
